import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DataSourceBuySyncAddonProps } from '@databox/ember-integrations/lib/components/DataSourceBuySyncAddon/DataSourceBuySyncAddon';

import type { PopupData, Service } from 'webapp-types';
import {
	BillingFeature,
	ModelName,
	SourceStatusLevel,
	SourceTypeCategory,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy, uniq } from 'webapp/utils/common';

import SourcePopupBuySyncAddonComponent from 'webapp/components/source/popup-buy-sync-addon';
import SourcePopupEnableCustomDataUpdateComponent from 'webapp/components/source/popup-enable-custom-data-update';
import SourcePopupPriorityDataUpdateComponent from 'webapp/components/source/popup-priority-data-update';
import SourcePopupResolveComponent from 'webapp/components/source/popup-resolve';
import SourcePopupSyncIntervalComponent from 'webapp/components/source/popup-sync-interval';

import type PopupModal from 'webapp/models-custom/popup/modal';
import Source from 'webapp/models-custom/source';
import type SourceType from 'webapp/models-custom/source-type';
import { SourceOption } from 'webapp/models-custom/source-type';
import { StatusObjectAction } from 'webapp/models-custom/status-object';
import type SourceRecord from 'webapp/models/source';

import { Severity } from 'webapp/services/logger';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export interface SourceListEntry {
	value: string;
	label: string;
	isGeneral: boolean;
	type: number;
	isStable: boolean;
	isBeta: boolean;
	isSoon: boolean;
	avatar: string;
	group: string;
	hideInDropdown?: boolean;
	// generic only
	profile?: string;
	isHidden?: boolean;
	// specific only
	label2?: string;
	label3?: string;
	statusLevel?: string;
	isDefault?: boolean;
	wrapperFor?: string;
	createdByName?: string;
	createdOnRelative?: string;
	canReauthorize?: boolean;
	statusActionElementHtml?: string;
	isDataset?: boolean;
	isMergedDataset?: boolean;
	parentSpaceAccessId?: number; // for datasets
	isSQLType?: boolean;
	affixIcon?: IconName;
	isDisabled?: boolean;
	isSheets?: boolean;
	prefixIcon?: IconName;
	isAbstract?: boolean;
	statusObjectAction?: StatusObjectAction;
}

export interface SourceInfoForSyncIntervalModal {
	id: number;
	name: string;
	createdBy: string;
	syncInterval: number;
	avatar: string;
	ownerId: number;
}

type ChangeSyncIntervalUpdateFrequency =
	DataSourceBuySyncAddonProps['dataSourcesSyncFrequencies'][number];

export const enum SourcesPusherEvents {
	SOURCE_SYNC_STATUS_UPDATED = 'DataSourceController.sourceSyncStatusUpdated',
}

export default class ModelManagerSourcesService extends ModelManagerForStoreService<
	SourceRecord,
	Source
> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	defaultTimezone = 'Etc/UTC';

	getSourceTypeKey(id: string): string | null {
		const source = this.savedRecords.find((item) => item.id === id);
		let typeKey;

		if (source) {
			if (source.type) {
				typeKey = this.mmSourceTypesService.findModelById(source.type)?.key;
			} else if (source.wrapperFor) {
				typeKey = source.wrapperFor;
			} else {
				typeKey = 'Custom';
			}
		}

		return typeKey || null;
	}

	@cached
	get datasetSpreadsheetSourceCount(): number {
		return this.sourceListSpecific.filter((source) => source.isDataset && source.isSheets)
			.length;
	}

	private _sourceModels: Source[] = [];

	@cached
	get sourceModels(): Source[] {
		const isChanged = this.updateModelCache(this._sourceModels, this.savedRecords);
		if (isChanged) {
			next(() => {
				this.mmMetricsService.updateMetricsFromSources();
			});
		}
		return this._sourceModels;
	}

	get pushableSourceRecords(): SourceRecord[] {
		return this.savedRecords.filter((item) => item.token && !item.wrapperFor);
	}

	@cached
	get defaultSourceId(): string {
		const record = this.savedRecords.find((s) => s.isRoot);
		return record?.id || '';
	}

	@cached
	get defaultSource(): Source | null {
		const sourceRecord = this.savedRecords.find((s) => s.isRoot);
		return sourceRecord ? this.createModelFromRecord(sourceRecord) : null;
	}

	@cached
	get myReconnectableSourcesCount(): number {
		let count = 0;
		if (this.navigationService.dataLoader.initialRequestsDone) {
			this.sourceModels.forEach((source) => {
				if (
					(source.isMine || source.isOwnedByMe) &&
					(source.canReconnect || source.canReauthorize)
				) {
					count++;
				}
			});
		}
		return count;
	}

	@cached
	get hasConnectedSources(): boolean {
		return this.getHasConnectedSources(true);
	}

	// the user is pushing in the default token (any user metrics there)
	get hasDefaultTokenMetrics(): boolean {
		const defaultSourceId = this.defaultSourceId;
		return (
			!!defaultSourceId &&
			this.mmMetricsService.getMetricsForSpaceAccessId(+defaultSourceId).length > 1
		);
	}

	getHasConnectedSources(includeConnectRequests = false): boolean {
		let hasConnectedSources = this.connectedSourcesCount > 0;

		if (!hasConnectedSources) {
			hasConnectedSources = this.hasDefaultTokenMetrics;
		}

		if (!hasConnectedSources && includeConnectRequests) {
			hasConnectedSources = this.appNotificationsService.connectRequests.length > 0;
		}

		return hasConnectedSources;
	}

	get datasetSourcesCount() {
		return this.savedRecords.filter(
			(item) => item.selectedSourceOption === SourceOption.DATASETS,
		).length;
	}

	get mergedDatasetSourcesCount() {
		return this.savedRecords.filter(
			(item) => item.selectedSourceOption === SourceOption.MERGED_DATASETS,
		).length;
	}

	@cached
	get hasConnectedDatasets(): boolean {
		return this.datasetSourcesCount > 0;
	}

	@cached
	get hasConnectedMergedDatasets(): boolean {
		return this.mergedDatasetSourcesCount > 0;
	}

	get connectedSourcesCount() {
		return this.savedRecords.filter((item) => !item.isRoot).length;
	}

	get connectedCloudSourceModels() {
		return this.sourceModels.filter(
			(source) =>
				source.sourceType.category === SourceTypeCategory.CLOUD &&
				source.statusLevel === SourceStatusLevel.ACTIVE &&
				!source.isRoot,
		);
	}

	get connectedSourceTypeIdsOrKeys(): (string | number)[] {
		const connectedTypes: (string | number)[] = [];
		this.savedRecords.forEach((source) => {
			const uniqueId = source.type || source.wrapperFor || 'Custom';
			connectedTypes.push(uniqueId);
		});
		return uniq(connectedTypes);
	}

	get firstConnectedCloudSourceType(): SourceType | null {
		const firstConnectedCloudSource = sortBy(
			this.sourceModels.filter(
				(source) => source.sourceType.category === SourceTypeCategory.CLOUD,
			),
			'id',
		)[0];

		return firstConnectedCloudSource?.sourceType || null;
	}

	findSourceTypeForSourceId(sourceId: string): SourceType | null {
		const sourceRecord = this.findRecord(sourceId);
		if (!sourceRecord) {
			return null;
		}
		const sourceType = this.findSourceTypeForSourceRecord(sourceRecord);
		if (!sourceType) {
			return null;
		}
		return sourceType;
	}

	findSourceTypeForSourceRecord(record: SourceRecord): SourceType | null {
		let sourceType: SourceType | null = null;
		const type = record.type;
		const isIngestionType = this.isIngestionType(record);

		if (type) {
			sourceType = this.mmSourceTypesService.findModelById(type);
		} else if (isIngestionType) {
			sourceType = this.mmSourceTypesService.findModelByKey('DataboxAPI');
		} else {
			sourceType = this.mmSourceTypesService.findModelByKey(record.wrapperFor || 'Custom');
		}

		if (!sourceType) {
			this.loggerService.logError(
				'Missing genericSource',
				{
					sourceId: record.id,
					type: record.type,
					wrapperFor: record.wrapperFor,
				},
				Severity.WARNING,
			);

			if (window.env !== 'local') {
				this.navigationService.openReloadModal();
			}

			return null;
		}

		return sourceType;
	}

	isIngestionType(record: SourceRecord): boolean {
		return (
			record.type === 0 &&
			!record.wrapperFor &&
			[SourceOption.DATASETS, SourceOption.ABSTRACT].includes(record.selectedSourceOption)
		);
	}

	// ------ Formatted source listings ------

	@cached
	get sourceTypesNotConnected(): SourceType[] {
		const list: SourceType[] = [],
			alreadyConnectedTypes = this.connectedSourceTypeIdsOrKeys;

		sortBy(this.mmSourceTypesService.connectableSourceTypeModels, 'name').forEach(
			(sourceType) => {
				const showOnList = !sourceType.isGroup;

				const mainId = sourceType.id || sourceType.key;
				// skip dummies and already connected types
				if (showOnList && !alreadyConnectedTypes.includes(mainId)) {
					list.push(sourceType);
				}
			},
		);

		return list;
	}

	@cached
	get sourceListGeneric(): SourceListEntry[] {
		return this.sourceTypesNotConnected.map((st) => this.formatSourceTypeForList(st));
	}

	@cached
	get sourceListSpecific(): SourceListEntry[] {
		const sortedSources = this.sortSourcesWithDatasets(this.sourceModels);
		const list: SourceListEntry[] = [];

		sortedSources.forEach((source) => {
			const sourceType = source.sourceType;

			let sameGroup: SourceRecord[] = [];
			if (source.isWrapper) {
				sameGroup = this.savedRecords.filter(
					(item) => item.wrapperFor === source.wrapperFor,
				);
			} else if (source.type) {
				sameGroup = this.savedRecords.filter((item) => item.type === source.type);
			} else {
				sameGroup = this.savedRecords.filter((item) => !item.type && !item.wrapperFor);
			}

			const hasStatusAction =
				source.statusObject.canReauthorize ||
				source.statusObject.canReconnect ||
				source.statusObject.isActionSourceConnected ||
				source.isDatasetWithAction;

			const statusActionElementHtml = hasStatusAction
				? source.statusObject.getStatusActionHTML()
				: '';

			const label = source.isMergedDataset
				? 'Merged datasets'
				: sourceType?.name || source.subtitle || '';

			const datasetParent = source.parentInfo;

			let label3;
			if (source.isSourceDisabledForUsage) {
				if (hasStatusAction && !source.isTypeOfDataset) {
					label3 = `<span>${source.sourceType.name}</span>&nbsp;<ui-icon name="AlertFill" size="sm" class="warn" title="${source.statusTitle}"></ui-icon>`;
				} else {
					const statusTitle = source.isDatasetSyncInProgress
						? 'Sync in progress'
						: !source.lastSync?.lastSuccessfulSyncAt
							? 'No data available'
							: source.statusTitle;
					label3 = `${datasetParent ? `<span>${datasetParent?.title}</span>&nbsp;` : ''}<ui-icon name="AlertFill" size="sm" class="warn" title="${statusTitle}"></ui-icon>`;
				}
			} else if (source.isTypeOfDataset) {
				if (source.isDataset) {
					label3 = `<span>${datasetParent?.title}</span>`;
				} else if (source.isMergedDataset) {
					label3 = `<span>Merged dataset</span>`;
				}
			} else {
				label3 = `<span>${source.sourceType.name}</span>`;
			}

			let affixIcon: IconName | undefined;
			if (source.isTypeOfDataset) {
				affixIcon = source.isMergedDataset ? 'Merge' : 'Table';
			}

			const sourceData: SourceListEntry = {
				value: source.id!,
				label,
				label2: source.title,
				label3,
				isGeneral: false,
				statusLevel: source.statusLevel,
				type: source.type,
				isStable: sourceType?.isStable,
				isBeta: sourceType?.isBeta,
				isSoon: sourceType?.isSoon,
				avatar: source.realAvatar,
				group: 'Other connected sources',
				isDefault: source.isRoot,
				wrapperFor: source.isWrapper ? source.wrapperFor! : '',
				createdByName: source.createdBy?.name,
				createdOnRelative: source.createdRelative,
				canReauthorize: source.canReauthorize,
				statusActionElementHtml,
				isDataset: source.isDataset,
				isMergedDataset: source.isMergedDataset,
				parentSpaceAccessId: datasetParent?.spaceAccessId,
				isSQLType: source.isSQLType,
				affixIcon,
				isDisabled: source.isSourceDisabledForUsage,
				isSheets: source.isSheets,
				prefixIcon: source.isDataset ? 'ArrowDownRight' : undefined,
				isAbstract: source.isAbstract,
				statusObjectAction: source.statusObject.action,
			};

			// if more than one, group them by label
			if (sameGroup.length > 1) {
				sourceData.group = sourceData.label;
				if (sourceData.label2) {
					sourceData.label = sourceData.label2;
					delete sourceData.label2;
				}
			}

			list.push(sourceData);
		});

		return list;
	}

	/**
	 * Custom sorting method that groups datasets with their parent data sources.
	 * Datasets appear immediately after their parent data source.
	 */
	sortSourcesWithDatasets(
		list: Source[],
		soryByKey1: keyof Source = 'title',
		soryByKey2?: keyof Source,
	): Source[] {
		const dataSources: Source[] = [];
		const datasets: Source[] = [];
		const mergedDatasets: Source[] = [];

		list.forEach((source) => {
			if (source.isMergedDataset) {
				mergedDatasets.push(source);
			} else if (source.isDataset) {
				datasets.push(source);
			} else {
				dataSources.push(source);
			}
		});

		const sortedDataSources = sortBy(dataSources, soryByKey1, soryByKey2);
		const parentToDatasetsMap = new Map<string, Source[]>();

		datasets.forEach((dataset) => {
			if (dataset.parentInfo?.spaceAccessId) {
				const parentId = dataset.parentInfo.spaceAccessId.toString();
				if (!parentToDatasetsMap.has(parentId)) {
					parentToDatasetsMap.set(parentId, []);
				}
				parentToDatasetsMap.get(parentId)!.push(dataset);
			}
		});

		const sortedList: Source[] = [];

		sortedDataSources.forEach((dataSource) => {
			sortedList.push(dataSource);

			const sourceDatasets = parentToDatasetsMap.get(dataSource.id!);
			if (sourceDatasets) {
				const sortedDatasets = sortBy(sourceDatasets, soryByKey1, soryByKey2);
				sortedList.push(...sortedDatasets);
			}
		});

		sortedList.push(...sortBy(mergedDatasets, soryByKey1, soryByKey2));

		return sortedList;
	}

	@cached
	get sourceList(): SourceListEntry[] {
		return [...this.sourceListSpecific, ...this.sourceListGeneric];
	}

	@cached
	get sourceListGrouped(): SourceListEntry[] {
		// first check if we hide any (empty)
		const sourceListCopy = this.sourceList.map((sourceEntry) => {
			let isHidden = false;
			if (sourceEntry.isHidden) {
				isHidden = true;
			} else if (!sourceEntry.type && !sourceEntry.wrapperFor && !sourceEntry.isGeneral) {
				let metrics = this.mmMetricsService.getMetricsForSpaceAccessId(+sourceEntry.value);

				// show tokens only if they have at least 1 metric inside
				if (sourceEntry.isDefault) {
					// exclude scripts
					metrics = metrics.filter((metric) => {
						return !metric.isTypeMetricCalculation;
					});
				}

				isHidden = metrics.length === 0;
			}
			const sourceEntryCopy = Object.assign({}, sourceEntry);
			sourceEntryCopy.hideInDropdown = isHidden;

			return sourceEntryCopy;
		});

		// force every item to be a group for itself
		sourceListCopy.forEach((sourceEntry) => {
			if (sourceEntry.group === 'Other connected sources') {
				sourceEntry.group = sourceEntry.label;

				if (sourceEntry.label2) {
					sourceEntry.label = sourceEntry.label2;
					delete sourceEntry.label2;
				}
			}
		});

		const notConnectedSourceList = sourceListCopy.filter((item) => item.isGeneral),
			connectedSourceList = sortBy(
				sourceListCopy.filter((item) => !item.isGeneral),
				'group',
			);

		return [...notConnectedSourceList, ...connectedSourceList];
	}

	formatSourceTypeForList(sourceType: SourceType, groupName = 'Not connected'): SourceListEntry {
		// since we filter only connectable source types, id will surely be a number
		const sourceTypeId = sourceType.id!;
		return {
			value: sourceType.key,
			label: sourceType.name,
			profile: '',
			isGeneral: true,
			type: sourceTypeId,
			isHidden: !!sourceType.isHidden,
			isStable: sourceType.isStable,
			isBeta: sourceType.isBeta,
			isSoon: sourceType.isSoon,
			avatar: sourceType.avatar,
			group: groupName,
		};
	}

	openResolvePopup(props: PopupData.Source.Resolve): PopupModal {
		const popup = this.popupService.createModal(SourcePopupResolveComponent, {
			popupClass: 'popup-type-resolve-transfer',
			allowStacked: true, // source/connect/popup
		});

		popup.open(props);

		return popup;
	}

	// ---- Sync interval change ----
	private handleChangeSyncIntervalError(error: Error) {
		if (error?.message) {
			this.infolayerService.error(error.message);
		}
	}

	private handleChangeSyncIntervalSuccess() {
		this.infolayerService.ok('Data source(s) updated successfully');
	}

	openSyncAddonBuyDialog(
		popupService: Service.Popup,
		dataSourceSyncFrequencies: ChangeSyncIntervalUpdateFrequency[],
		orderAmount?: number,
		trackingOrigin?: string,
	) {
		const buySyncAddonModal = popupService.createModal(SourcePopupBuySyncAddonComponent, {
			popupClass: 'popup-type-buy-sync-interval',
		});

		const handleOpenBilling = () => {
			void buySyncAddonModal.close();
			this.navigationService.transitionToRoute('settings.billing.plans');

			this.analyticsService.event(TrackingEvent.UPGRADE_INITIATED, {
				origin: trackingOrigin,
			});
		};

		this.analyticsService.event(TrackingEvent.LIMITATION_POPUP, {
			origin: trackingOrigin,
		});

		const accountOwner = this.accountService.accountOwner;

		const buySyncIntervalModalProps: PopupData.Source.BuySyncIntervalAddon = {
			dataSourcesSyncFrequencies: dataSourceSyncFrequencies,
			orderAmount: orderAmount,
			accessTokenFactory: () => this.currentUserService.accessToken ?? '',
			spaceIdFactory: () => this.accountService.id,
			amaHost: this.requestService.amaBackendUrl,
			isUserAdmin: this.accountService.isAdminButNotInClientSpace,
			accountOwner: {
				name: accountOwner?.name ?? '',
				email: accountOwner?.email ?? '',
				avatar: accountOwner?.avatar ?? '',
			},
			isDarkThemeActive: this.navigationService.theme.isDark,
			onOpenBillingPage: handleOpenBilling,
			onError: (error: Error) => this.handleChangeSyncIntervalError(error),
			onClose: () => {
				void buySyncAddonModal.close();
			},
			onDataSourcesUpdated: () => {
				void buySyncAddonModal.close();
				this.handleChangeSyncIntervalSuccess();
			},
			trackingOrigin: trackingOrigin ?? '',
		};

		buySyncAddonModal.open(buySyncIntervalModalProps);
	}

	openSyncIntervalModal(
		selectedDataSource?: SourceInfoForSyncIntervalModal,
		trackingOrigin?: string,
	) {
		if (!this.currentUserService.numericId) {
			return;
		}

		const changeIntervalModal = this.popupService.createModal(
			SourcePopupSyncIntervalComponent,
			{
				popupClass: 'popup-type-sync-interval',
			},
		);

		const changeIntervalModalProps: PopupData.Source.ChangeSyncInterval = {
			accessTokenFactory: () => this.currentUserService.accessToken ?? '',
			spaceIdFactory: () => this.accountService.id,
			amaHost: this.requestService.amaBackendUrl,
			dataSource: selectedDataSource,
			isDarkThemeActive: this.navigationService.theme.isDark,
			isWhitelabeled: this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins,
			user: {
				id: this.currentUserService.numericId,
				isAdmin: this.currentUserService.isAdmin,
			},
			onOpenBuyDialog: (dataSourceSyncFrequency: ChangeSyncIntervalUpdateFrequency) =>
				this.openSyncAddonBuyDialog(
					this.popupService,
					[dataSourceSyncFrequency],
					undefined,
					trackingOrigin,
				),
			onClose: () => {
				void changeIntervalModal.close();
			},
			onError: (error: Error) => this.handleChangeSyncIntervalError(error),
			onDataSourceUpdated: () => this.handleChangeSyncIntervalSuccess(),
			trackingOrigin: trackingOrigin ?? '',
		};

		changeIntervalModal.open(changeIntervalModalProps);
	}

	openEnableCustomDataUpdatePopup(
		selectedDataSources: Source[],
		isLimitReached: boolean,
		trackingOrigin?: string,
	) {
		const billingRefreshInterval = this.billingService.features.getValue(
			BillingFeature.DATA_REFRESH_INTERVAL,
		);

		const dataSources: PopupData.Source.EnableCustomDataUpdate['dataSources'] = [];
		selectedDataSources?.forEach((source) => {
			const syncInterval = source.customSyncInterval ?? billingRefreshInterval;
			dataSources.push({
				id: source.numericId!,
				name: source.title,
				createdBy: source.createdBy?.name || '',
				syncInterval,
				avatar: source.realAvatar,
				ownerId: source.createdById,
				dataSourceTypeName: source.isMergedDataset
					? 'Merged dataset'
					: source.sourceType.name,
				selectedSourceOption: source.selectedSourceOption,
			});
		});

		const enableCustomDataUpdateModal = this.popupService.createModal(
			SourcePopupEnableCustomDataUpdateComponent,
			{
				popupClass: 'popup-type-enable-custom-data-update',
			},
		);

		const enableCustomDataUpdateModalProps: PopupData.Source.EnableCustomDataUpdate = {
			accessTokenFactory: () => this.currentUserService.accessToken ?? '',
			spaceIdFactory: () => this.accountService.id,
			amaHost: this.requestService.amaBackendUrl,
			dataSources: dataSources,
			isDarkThemeActive: this.navigationService.theme.isDark,
			user: {
				id: this.currentUserService.numericId!,
				isAdmin: this.currentUserService.isAdmin,
			},
			onOpenBuyDialog: (
				dataSourceSyncFrequencies: ChangeSyncIntervalUpdateFrequency[],
				orderAmount: number,
			) =>
				this.openSyncAddonBuyDialog(
					this.popupService,
					dataSourceSyncFrequencies,
					orderAmount,
					trackingOrigin,
				),
			onClose: () => {
				void enableCustomDataUpdateModal.close();
			},
			onError: (error: Error) => this.handleChangeSyncIntervalError(error),
			onDataSourcesUpdated: () => this.handleChangeSyncIntervalSuccess(),
			trackingOrigin: trackingOrigin ?? '',
			isLimitReached,
		};

		enableCustomDataUpdateModal.open(enableCustomDataUpdateModalProps);
	}

	openContactOwnerPopup() {
		const isClientOrWhitelabeled =
			this.accountService.isClient || this.accountService.whiteLabelingEnabled;

		const enablePriorityDataUpdateModal = this.popupService.createModal(
			SourcePopupPriorityDataUpdateComponent,
			{
				popupClass: 'popup-type-priority-data-update',
			},
		);

		const accountOwner = this.accountService.accountOwner;

		const enablePriorityDataUpdateModalProps: PopupData.Source.PriorityDataUpdate = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			isClientOrWLAccount: isClientOrWhitelabeled,
			accountOwner: {
				name: accountOwner?.name ?? '',
				email: accountOwner?.email ?? '',
				avatar: accountOwner?.avatar ?? '',
			},
		};

		enablePriorityDataUpdateModal.open(enablePriorityDataUpdateModalProps);
	}

	// ------ For-store ------

	modelName = ModelName.SOURCE;
	override modelTitle = 'data source';

	createModel(model: SourceRecord): Source {
		return new Source({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/sources': ModelManagerSourcesService;
	}
}
